import { useState, useMemo, useEffect, useRef } from 'react';
import { X, Search, Loader2, Check, RotateCcw, Plus } from 'lucide-react';
import { companyCarsApi } from '../../services/companyCarsApi';
import { isEosCompany } from '../../utils/bcLinks';
import type {
    CompanyCar,
    CarBrand,
    CarFuelType,
    CarLeasingCompany,
    CarInsuranceCompany,
    CarEmployee,
    CompanyOption,
    FixedAssetOption,
    CreateCarData,
    UpdateCarData,
    SpecLookupResult,
} from '../../types/companyCars';

interface CarFormModalProps {
    car?: CompanyCar | null;
    brands: CarBrand[];
    fuelTypes: CarFuelType[];
    leasingCompanies: CarLeasingCompany[];
    insuranceCompanies: CarInsuranceCompany[];
    employees: CarEmployee[];
    companies: CompanyOption[];
    onSave: (data: CreateCarData & Pick<UpdateCarData, 'moveDocuments'>) => Promise<void>;
    onClose: () => void;
    onCreateBrand: (name: string) => Promise<number>;
    onCreateLeasingCompany: (name: string) => Promise<number>;
    onCreateInsuranceCompany: (name: string) => Promise<number>;
}

export default function CarFormModal({
    car, brands, fuelTypes, leasingCompanies, insuranceCompanies, employees, companies,
    onSave, onClose, onCreateBrand, onCreateLeasingCompany, onCreateInsuranceCompany,
}: CarFormModalProps) {
    const isEdit = !!car;

    const [form, setForm] = useState<CreateCarData>({
        companyCode: car?.CompanyCode || '',
        staffId: car?.StaffID || null,
        driverName: car?.DriverName || '',
        driverShort: car?.DriverShort || '',
        staffNumber: car?.StaffNumber || '',
        driverEmail: car?.DriverEmail || '',
        carBrandId: car?.CarBrandId || null,
        carModel: car?.CarModel || '',
        licensePlate: car?.LicensePlate || '',
        carId: car?.CarID || '',
        vin: car?.VIN || '',
        fixedAssetNo: car?.FixedAssetNo || '',
        matriculationDate: car?.MatriculationDate?.slice(0, 10) || '',
        endOfUse: car?.EndOfUse?.slice(0, 10) || '',
        fuelTypeId: car?.FuelTypeId || null,
        kwhPer100km: car?.KwhPer100km ?? null,
        fuelLPer100km: car?.FuelLPer100km ?? null,
        co2WltpKgPerKm: car?.Co2WltpKgPerKm ?? null,
        isLeasing: car?.IsLeasing || false,
        leasingCompanyId: car?.LeasingCompanyId || null,
        leasingDurationMonths: car?.LeasingDurationMonths ?? null,
        leasingContractKm: car?.LeasingContractKm ?? null,
        leasingExpiryDate: car?.LeasingExpiryDate?.slice(0, 10) || '',
        currentMileageKm: car?.CurrentMileageKm ?? null,
        purchasePrice: car?.PurchasePrice ?? null,
        leasingRate: car?.LeasingRate ?? null,
        insurance: car?.Insurance || '',
        insuranceCompanyId: car?.InsuranceCompanyId || null,
        insuranceType: car?.InsuranceType || '',
        insuranceDeductible: car?.InsuranceDeductible ?? null,
        notes: car?.Notes || '',
    });

    const [saving, setSaving] = useState(false);
    // Documents hang on CompanyCode + PlateKey (migration 224). This restates the DB rule only to
    // decide whether to OFFER the move; the server compares the real keys before moving anything.
    const plateKeyOf = (plate: string | null | undefined) => {
        if (!plate || plate.includes('?')) return null;
        return plate.replace(/[ \-.\t –]/g, '').toUpperCase() || null;
    };
    const [moveDocuments, setMoveDocuments] = useState(true);
    const keyChanged = !!car && car.DocumentCount > 0 && !!car.PlateKey
        && (plateKeyOf(form.licensePlate) !== car.PlateKey || form.companyCode !== car.CompanyCode);
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

    // Brand inline add
    const [addingBrand, setAddingBrand] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');

    // Leasing company inline add
    const [addingLeasing, setAddingLeasing] = useState(false);
    const [newLeasingName, setNewLeasingName] = useState('');

    // Insurance inline add
    const [addingInsurance, setAddingInsurance] = useState(false);
    const [newInsuranceName, setNewInsuranceName] = useState('');

    // Fixed assets
    const [fixedAssets, setFixedAssets] = useState<FixedAssetOption[]>([]);
    const [faSearch, setFaSearch] = useState('');
    const [showFaDropdown, setShowFaDropdown] = useState(false);

    // Refs for click-outside
    const employeeRef = useRef<HTMLDivElement>(null);
    const faRef = useRef<HTMLDivElement>(null);

    // Close dropdowns on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (employeeRef.current && !employeeRef.current.contains(e.target as Node)) {
                setShowEmployeeDropdown(false);
            }
            if (faRef.current && !faRef.current.contains(e.target as Node)) {
                setShowFaDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // AI Spec Lookup
    const [specLoading, setSpecLoading] = useState(false);
    const [specSuggestion, setSpecSuggestion] = useState<SpecLookupResult | null>(null);
    const [specAccepted, setSpecAccepted] = useState<Record<string, boolean>>({});

    const selectedBrand = brands.find(b => b.Id === form.carBrandId);
    const isEos = form.companyCode ? isEosCompany(form.companyCode) : false;

    // The Active toggle writes EndOfUse (the non-writable computed IsActive column can't be set).
    // Toggle semantics: Active = no End-of-Use date, or one that still lies in the future.
    // Toggling off stamps today's date, which makes the car inactive from today on.
    // This mirrors the DB computed column exactly (migration 129:
    // EndOfUse IS NULL OR EndOfUse > today), so the label here and the badge in the
    // fleet list can never disagree.
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const _now = new Date();
    const todayStr = `${_now.getFullYear()}-${pad2(_now.getMonth() + 1)}-${pad2(_now.getDate())}`;
    const isActiveNow = !form.endOfUse || form.endOfUse.slice(0, 10) > todayStr;

    // Leasing mileage vs contract comparison (Zusatzkosten indicator)
    const kmOverContract = (form.leasingContractKm != null && form.currentMileageKm != null)
        ? form.currentMileageKm - form.leasingContractKm
        : null;

    // Load fixed assets when company changes
    useEffect(() => {
        if (form.companyCode && isEosCompany(form.companyCode)) {
            companyCarsApi.getFixedAssets(form.companyCode).then(setFixedAssets).catch(() => setFixedAssets([]));
        } else {
            setFixedAssets([]);
        }
    }, [form.companyCode]);

    const filteredEmployees = useMemo(() => {
        if (!employeeSearch.trim()) return employees.slice(0, 30);
        const term = employeeSearch.toLowerCase();
        return employees.filter(e =>
            e.FullName.toLowerCase().includes(term) ||
            e.EMail?.toLowerCase().includes(term)
        ).slice(0, 30);
    }, [employees, employeeSearch]);

    const filteredAssets = useMemo(() => {
        if (!faSearch.trim()) return fixedAssets.slice(0, 30);
        const term = faSearch.toLowerCase();
        return fixedAssets.filter(a =>
            a.FixedAssetCode.toLowerCase().includes(term) ||
            a.Description.toLowerCase().includes(term)
        ).slice(0, 30);
    }, [fixedAssets, faSearch]);

    const setField = <K extends keyof CreateCarData>(key: K, value: CreateCarData[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const selectEmployee = (emp: CarEmployee) => {
        setForm(prev => ({
            ...prev,
            staffId: emp.StaffID,
            driverName: `${emp.LastName} ${emp.FirstName}`.trim(),
            driverShort: emp.Short || '',
            staffNumber: emp.StaffID,
            driverEmail: emp.EMail || '',
            companyCode: emp.CompanyCode || prev.companyCode,
        }));
        setEmployeeSearch('');
        setShowEmployeeDropdown(false);
    };

    const handleAddBrand = async () => {
        if (!newBrandName.trim()) return;
        const newId = await onCreateBrand(newBrandName.trim());
        setField('carBrandId', newId);
        setNewBrandName('');
        setAddingBrand(false);
    };

    const handleAddLeasing = async () => {
        if (!newLeasingName.trim()) return;
        const newId = await onCreateLeasingCompany(newLeasingName.trim());
        setField('leasingCompanyId', newId);
        setNewLeasingName('');
        setAddingLeasing(false);
    };

    const handleAddInsurance = async () => {
        if (!newInsuranceName.trim()) return;
        const newId = await onCreateInsuranceCompany(newInsuranceName.trim());
        setField('insuranceCompanyId', newId);
        setNewInsuranceName('');
        setAddingInsurance(false);
    };

    const handleLookupSpecs = async () => {
        if (!selectedBrand || !form.carModel?.trim()) return;
        setSpecLoading(true);
        setSpecSuggestion(null);
        setSpecAccepted({});
        try {
            const result = await companyCarsApi.lookupSpecs(selectedBrand.BrandName, form.carModel.trim());
            setSpecSuggestion(result);
            if (result.kwhPer100km != null) { setSpecAccepted(prev => ({ ...prev, kwh: true })); setField('kwhPer100km', result.kwhPer100km); }
            if (result.fuelLPer100km != null) { setSpecAccepted(prev => ({ ...prev, fuel: true })); setField('fuelLPer100km', result.fuelLPer100km); }
            if (result.co2WltpKgPerKm != null) { setSpecAccepted(prev => ({ ...prev, co2: true })); setField('co2WltpKgPerKm', result.co2WltpKgPerKm); }
        } catch { /* toast handled */ }
        finally { setSpecLoading(false); }
    };

    const rejectSpec = (field: 'kwh' | 'fuel' | 'co2') => {
        setSpecAccepted(prev => ({ ...prev, [field]: false }));
        if (field === 'kwh') setField('kwhPer100km', car?.KwhPer100km ?? null);
        if (field === 'fuel') setField('fuelLPer100km', car?.FuelLPer100km ?? null);
        if (field === 'co2') setField('co2WltpKgPerKm', car?.Co2WltpKgPerKm ?? null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.companyCode) return;
        setSaving(true);
        try { await onSave(keyChanged && moveDocuments ? { ...form, moveDocuments: true } : form); onClose(); }
        catch { /* toast handled */ }
        finally { setSaving(false); }
    };

    const inputCls = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500';
    const readonlyCls = 'w-full px-2 py-1 text-sm border border-gray-200 rounded bg-gray-50 text-gray-600';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-0.5';
    const specHighlight = (field: string) => specSuggestion && specAccepted[field] ? 'ring-2 ring-green-400 bg-green-50' : '';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center gap-4">
                        <h2 className="text-lg font-semibold">{isEdit ? 'Edit Car' : 'Add Car'}</h2>
                        {isEdit && (
                            <label className="flex items-center gap-2 cursor-pointer" title="Toggling off sets End of Use to today; toggling on clears it">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        checked={isActiveNow}
                                        onChange={e => setField('endOfUse', e.target.checked ? '' : todayStr)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors" />
                                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                                </div>
                                <span className={`text-sm font-medium ${isActiveNow ? 'text-green-700' : 'text-red-600'}`}>
                                    {isActiveNow ? 'Active' : 'Inactive'}
                                </span>
                            </label>
                        )}
                    </div>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-3">
                    {/* Driver Section */}
                    <fieldset className="border border-gray-200 rounded p-3 space-y-2">
                        <legend className="text-sm font-semibold text-gray-700 px-1">Driver</legend>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="relative" ref={employeeRef}>
                                <label className={labelCls}>Employee</label>
                                <input
                                    type="text"
                                    value={employeeSearch || (form.driverName ? `${form.driverName}` : '')}
                                    onChange={e => { setEmployeeSearch(e.target.value); setShowEmployeeDropdown(true); }}
                                    onFocus={() => { setEmployeeSearch(''); setShowEmployeeDropdown(true); }}
                                    className={inputCls}
                                    placeholder="Search employee..."
                                />
                                {showEmployeeDropdown && filteredEmployees.length > 0 && (
                                    <div className="absolute z-20 w-full mt-1 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                                        {filteredEmployees.map(emp => (
                                            <button key={emp.StaffID} type="button" onClick={() => selectEmployee(emp)}
                                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex justify-between">
                                                <span>{emp.LastName} {emp.FirstName}</span>
                                                <span className="text-gray-400">{emp.CompanyCode}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className={labelCls}>Company *</label>
                                <select value={form.companyCode} onChange={e => setField('companyCode', e.target.value)} className={inputCls} required>
                                    <option value="">Select...</option>
                                    {companies.map(c => (
                                        <option key={c.CompanyCode} value={c.CompanyCode}>{c.CompanyName} ({c.CompanyCode})</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                            <div><label className={labelCls}>Name</label><input type="text" value={form.driverName || ''} className={readonlyCls} readOnly /></div>
                            <div><label className={labelCls}>Short</label><input type="text" value={form.driverShort || ''} className={readonlyCls} readOnly /></div>
                            <div><label className={labelCls}>Staff No.</label><input type="text" value={form.staffNumber || ''} className={readonlyCls} readOnly /></div>
                            <div><label className={labelCls}>Email</label><input type="text" value={form.driverEmail || ''} className={readonlyCls} readOnly /></div>
                        </div>
                    </fieldset>

                    {/* Vehicle Section */}
                    <fieldset className="border border-gray-200 rounded p-3 space-y-2">
                        <legend className="text-sm font-semibold text-gray-700 px-1">Vehicle</legend>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className={labelCls}>Brand</label>
                                <div className="flex items-center gap-1">
                                    {addingBrand ? (
                                        <div className="flex items-center gap-1 flex-1">
                                            <input type="text" value={newBrandName} onChange={e => setNewBrandName(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddBrand(); } if (e.key === 'Escape') setAddingBrand(false); }}
                                                className={inputCls} placeholder="New brand..." autoFocus />
                                            <button type="button" onClick={handleAddBrand} className="p-1 text-green-600"><Check className="w-4 h-4" /></button>
                                            <button type="button" onClick={() => setAddingBrand(false)} className="p-1 text-gray-400"><X className="w-4 h-4" /></button>
                                        </div>
                                    ) : (
                                        <>
                                            <select value={form.carBrandId || ''} onChange={e => setField('carBrandId', e.target.value ? Number(e.target.value) : null)} className={`${inputCls} flex-1`}>
                                                <option value="">Select...</option>
                                                {brands.filter(b => b.IsActive).map(b => (<option key={b.Id} value={b.Id}>{b.BrandName}</option>))}
                                            </select>
                                            <button type="button" onClick={() => setAddingBrand(true)} className="p-1 text-blue-500 hover:text-blue-700" title="Add new brand"><Plus className="w-4 h-4" /></button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <label className={labelCls}>Model</label>
                                <input type="text" value={form.carModel || ''} onChange={e => setField('carModel', e.target.value)} className={inputCls} />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><label className={labelCls}>License Plate</label><input type="text" value={form.licensePlate || ''} onChange={e => setField('licensePlate', e.target.value)} className={inputCls} /></div>
                            <div><label className={labelCls}>Car ID</label><input type="text" value={form.carId || ''} onChange={e => setField('carId', e.target.value)} className={inputCls} /></div>
                            <div><label className={labelCls}>VIN</label><input type="text" value={form.vin || ''} onChange={e => setField('vin', e.target.value)} className={inputCls} /></div>
                        </div>
                        {keyChanged && car && (
                            <label className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                <input type="checkbox" checked={moveDocuments} onChange={e => setMoveDocuments(e.target.checked)} className="mt-0.5" />
                                <span>
                                    Take the {car.DocumentCount} document(s) of {car.LicensePlate} along to the new
                                    {form.companyCode !== car.CompanyCode ? ' company and plate' : ' plate'}.
                                    Unticked, they stay with the old plate and no longer show on this car.
                                </span>
                            </label>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="relative" ref={faRef}>
                                <label className={labelCls}>Fixed Asset No.</label>
                                {isEos && fixedAssets.length > 0 ? (
                                    <>
                                        <input type="text" value={faSearch || form.fixedAssetNo || ''}
                                            onChange={e => { setFaSearch(e.target.value); setShowFaDropdown(true); }}
                                            onFocus={() => { setFaSearch(''); setShowFaDropdown(true); }}
                                            className={inputCls} placeholder="Search by code or description..." />
                                        {showFaDropdown && filteredAssets.length > 0 && (
                                            <div className="absolute z-20 w-full mt-1 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                                                {filteredAssets.map(a => (
                                                    <button key={a.FixedAssetCode} type="button"
                                                        onClick={() => { setField('fixedAssetNo', a.FixedAssetCode); setFaSearch(''); setShowFaDropdown(false); }}
                                                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50">
                                                        <span className="font-medium">{a.FixedAssetCode}</span>
                                                        <span className="ml-2 text-gray-400 truncate">{a.Description}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <input type="text" value={form.fixedAssetNo || ''} onChange={e => setField('fixedAssetNo', e.target.value)} className={inputCls} />
                                )}
                            </div>
                            <div><label className={labelCls}>Purchase Price</label><input type="number" step="1" value={form.purchasePrice ?? ''} onChange={e => setField('purchasePrice', e.target.value ? Number(e.target.value) : null)} className={inputCls} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={labelCls}>Matriculation Date</label><input type="date" value={form.matriculationDate || ''} onChange={e => setField('matriculationDate', e.target.value)} className={inputCls} /></div>
                            <div><label className={labelCls}>End of Use</label><input type="date" value={form.endOfUse || ''} onChange={e => setField('endOfUse', e.target.value)} className={inputCls} /></div>
                        </div>
                    </fieldset>

                    {/* Fuel & Emissions */}
                    <fieldset className="border border-gray-200 rounded p-3 space-y-2">
                        <legend className="text-sm font-semibold text-gray-700 px-1">Fuel & Emissions</legend>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Fuel Type</label>
                                <select value={form.fuelTypeId || ''} onChange={e => setField('fuelTypeId', e.target.value ? Number(e.target.value) : null)} className={inputCls}>
                                    <option value="">Select...</option>
                                    {fuelTypes.filter(f => f.IsActive).map(f => (<option key={f.Id} value={f.Id}>{f.FuelLabel}</option>))}
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button type="button" onClick={handleLookupSpecs} disabled={!selectedBrand || !form.carModel?.trim() || specLoading}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed">
                                    {specLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                    {specLoading ? 'Searching...' : 'AI Lookup Specs'}
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className={labelCls}>kWh/100km</label>
                                <div className="flex items-center gap-1">
                                    <input type="number" step="0.1" value={form.kwhPer100km ?? ''} onChange={e => setField('kwhPer100km', e.target.value ? Number(e.target.value) : null)} className={`${inputCls} ${specHighlight('kwh')}`} />
                                    {specSuggestion && specAccepted.kwh && <button type="button" onClick={() => rejectSpec('kwh')} className="p-1 text-red-400 hover:text-red-600"><RotateCcw className="w-3.5 h-3.5" /></button>}
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Fuel l/100km</label>
                                <div className="flex items-center gap-1">
                                    <input type="number" step="0.1" value={form.fuelLPer100km ?? ''} onChange={e => setField('fuelLPer100km', e.target.value ? Number(e.target.value) : null)} className={`${inputCls} ${specHighlight('fuel')}`} />
                                    {specSuggestion && specAccepted.fuel && <button type="button" onClick={() => rejectSpec('fuel')} className="p-1 text-red-400 hover:text-red-600"><RotateCcw className="w-3.5 h-3.5" /></button>}
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>CO₂ WLTP (g/km)</label>
                                <div className="flex items-center gap-1">
                                    <input type="number" step="1" value={form.co2WltpKgPerKm != null ? Math.round(form.co2WltpKgPerKm * 1000) : ''} onChange={e => setField('co2WltpKgPerKm', e.target.value ? Number(e.target.value) / 1000 : null)} className={`${inputCls} ${specHighlight('co2')}`} />
                                    {specSuggestion && specAccepted.co2 && <button type="button" onClick={() => rejectSpec('co2')} className="p-1 text-red-400 hover:text-red-600"><RotateCcw className="w-3.5 h-3.5" /></button>}
                                </div>
                            </div>
                        </div>
                        {specSuggestion?.source && (
                            <p className="text-xs text-gray-400">Source: <a href={specSuggestion.source} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-500">{specSuggestion.source}</a></p>
                        )}
                    </fieldset>

                    {/* Leasing */}
                    <fieldset className="border border-gray-200 rounded p-3 space-y-2">
                        <legend className="text-sm font-semibold text-gray-700 px-1">Leasing</legend>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.isLeasing || false} onChange={e => setField('isLeasing', e.target.checked)} className="rounded" />
                            Leasing vehicle
                        </label>
                        {form.isLeasing && (
                            <>
                                <div className="grid grid-cols-6 gap-3">
                                    <div>
                                        <label className={labelCls}>Leasing Company</label>
                                        <div className="flex items-center gap-1">
                                            {addingLeasing ? (
                                                <div className="flex items-center gap-1 flex-1">
                                                    <input type="text" value={newLeasingName} onChange={e => setNewLeasingName(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddLeasing(); } if (e.key === 'Escape') setAddingLeasing(false); }}
                                                        className={inputCls} placeholder="New company..." autoFocus />
                                                    <button type="button" onClick={handleAddLeasing} className="p-1 text-green-600"><Check className="w-4 h-4" /></button>
                                                    <button type="button" onClick={() => setAddingLeasing(false)} className="p-1 text-gray-400"><X className="w-4 h-4" /></button>
                                                </div>
                                            ) : (
                                                <>
                                                    <select value={form.leasingCompanyId || ''} onChange={e => setField('leasingCompanyId', e.target.value ? Number(e.target.value) : null)} className={`${inputCls} flex-1`}>
                                                        <option value="">Select...</option>
                                                        {leasingCompanies.filter(l => l.IsActive).map(l => (<option key={l.Id} value={l.Id}>{l.CompanyName}</option>))}
                                                    </select>
                                                    <button type="button" onClick={() => setAddingLeasing(true)} className="p-1 text-blue-500 hover:text-blue-700" title="Add new"><Plus className="w-4 h-4" /></button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div><label className={labelCls}>Duration (months)</label><input type="number" value={form.leasingDurationMonths ?? ''} onChange={e => setField('leasingDurationMonths', e.target.value ? Number(e.target.value) : null)} className={inputCls} /></div>
                                    <div><label className={labelCls}>Contract km</label><input type="number" value={form.leasingContractKm ?? ''} onChange={e => setField('leasingContractKm', e.target.value ? Number(e.target.value) : null)} className={inputCls} /></div>
                                    <div><label className={labelCls}>Current km</label><input type="number" value={form.currentMileageKm ?? ''} onChange={e => setField('currentMileageKm', e.target.value ? Number(e.target.value) : null)} className={inputCls} /></div>
                                    <div><label className={labelCls}>Expiry Date</label><input type="date" value={form.leasingExpiryDate || ''} onChange={e => setField('leasingExpiryDate', e.target.value)} className={inputCls} /></div>
                                    <div><label className={labelCls}>Monthly Rate</label><input type="number" step="0.01" value={form.leasingRate ?? ''} onChange={e => setField('leasingRate', e.target.value ? Number(e.target.value) : null)} className={inputCls} /></div>
                                </div>
                                {kmOverContract != null && (
                                    <p className={`text-xs font-medium ${kmOverContract > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {kmOverContract > 0
                                            ? `${kmOverContract.toLocaleString('de-CH')} km over contract km — potential extra charges`
                                            : `${Math.abs(kmOverContract).toLocaleString('de-CH')} km under contract km`}
                                    </p>
                                )}
                            </>
                        )}
                    </fieldset>

                    {/* Insurance */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className={labelCls}>Insurance</label>
                            <div className="flex items-center gap-1">
                                {addingInsurance ? (
                                    <div className="flex items-center gap-1 flex-1">
                                        <input type="text" value={newInsuranceName} onChange={e => setNewInsuranceName(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddInsurance(); } if (e.key === 'Escape') setAddingInsurance(false); }}
                                            className={inputCls} placeholder="New insurance..." autoFocus />
                                        <button type="button" onClick={handleAddInsurance} className="p-1 text-green-600"><Check className="w-4 h-4" /></button>
                                        <button type="button" onClick={() => setAddingInsurance(false)} className="p-1 text-gray-400"><X className="w-4 h-4" /></button>
                                    </div>
                                ) : (
                                    <>
                                        <select value={form.insuranceCompanyId || ''} onChange={e => setField('insuranceCompanyId', e.target.value ? Number(e.target.value) : null)} className={`${inputCls} flex-1`}>
                                            <option value="">Select...</option>
                                            {insuranceCompanies.filter(i => i.IsActive).map(i => (<option key={i.Id} value={i.Id}>{i.CompanyName}</option>))}
                                        </select>
                                        <button type="button" onClick={() => setAddingInsurance(true)} className="p-1 text-blue-500 hover:text-blue-700" title="Add new"><Plus className="w-4 h-4" /></button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>Insurance Type</label>
                            <select value={form.insuranceType || ''} onChange={e => setField('insuranceType', e.target.value || null)} className={inputCls}>
                                <option value="">Select...</option>
                                <option value="Haftpflicht">Haftpflicht</option>
                                <option value="Teilkasko">Teilkasko</option>
                                <option value="Vollkasko">Vollkasko</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Deductible</label>
                            <input type="number" step="0.01" value={form.insuranceDeductible ?? ''} onChange={e => setField('insuranceDeductible', e.target.value ? Number(e.target.value) : null)} className={inputCls} placeholder="Selbstbehalt" />
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className={labelCls}>Notes</label>
                        <input type="text" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} className={inputCls} />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                        <button type="submit" disabled={saving || !form.companyCode}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            {isEdit ? 'Save Changes' : 'Add Car'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
